import {proxy, subscribe} from 'valtio';
import {provideDb} from './persistantDb';
import {deepMerge, deepClone} from './objectManagment';

import pkg from '../../../package.json';

// Uses application version and name as main keys to persist state in local storage
// Allow to reset users cache by updating package.json version
const APP_STATE_KEY = `${pkg.name}/${pkg.version}`;
const db = provideDb(APP_STATE_KEY);

/**
 * Simple persistence strategy
 * @param key - retrive and save proxy state based on key, must be unique per proxy definition
 * @param initialObject - initial object state, if application doesn't exists
 * @env NEXT_PUBLIC_IGNORE_STORAGE - will not save and not retrive application data from storage
 */
export const persist = <T extends object>(
  key: string,
  initialObject?: T,
): T => {
  if (needIgnoreStorage()) {
    return proxy(initialObject);
  }

  // Update intial object state fields with local storage copy
  // It is important to update, isntead of clone initial object,
  // because it can contain logic in methods and getters that need to preserve
  deepMerge(initialObject, db.get(key) || {});
  const state = proxy(initialObject) as T;

  subscribe(state, () => {
    // deep clone object without methods, getter and setters
    const cloned = deepClone(state);
    db.set(key, cloned);
  });

  return state;
};

const needIgnoreStorage = () =>
  process.env.NEXT_PUBLIC_IGNORE_STORAGE === 'true';
